import React, { useMemo, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, ShieldAlert, Search, UserRound, Phone, CalendarClock, Trophy, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { normalizePhone } from '../lib/store';
import { checkPhone } from '../lib/phoneVerify';
import type { AdminUser, AdminCatch } from '../lib/supabase';
interface AdminVerifyMemberProps {
  users: AdminUser[];
  catches: AdminCatch[];
}
/**
 * ADMIN-SIDE RECURRING-USER VERIFICATION (inline card, lives in the Verify tab).
 *
 * Staff use this when a person in front of them claims to be a returning
 * member. They type the person's name and/or phone; we look the phone up in the
 * shared SheetDB snapshot (so it works across devices) and return a clear
 * verdict — VERIFIED (with corroborating catch history) or NOT FOUND — so the
 * staffer can confidently match (or reject) the recurring claim.
 */
export function AdminVerifyMember({
  users,
  catches
}: AdminVerifyMemberProps) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [submitted, setSubmitted] = useState(false);
  // Exact match by normalized phone is the source of truth for "is recurring".
  const phoneMatch = useMemo<AdminUser | null>(() => {
    const target = normalizePhone(phone);
    if (!target) return null;
    return users.find(u => normalizePhone(u.phone) === target) ?? null;
  }, [phone, users]);
  // When no phone is given, offer name suggestions so staff can disambiguate.
  const nameSuggestions = useMemo<AdminUser[]>(() => {
    const q = name.trim().toLowerCase();
    if (!q || phone.trim()) return [];
    return users.filter(u => u.name.toLowerCase().includes(q)).slice(0, 5);
  }, [name, phone, users]);
  const verdictUser = phoneMatch;
  const priorCatches = useMemo<AdminCatch[]>(() => {
    if (!verdictUser) return [];
    const target = normalizePhone(verdictUser.phone);
    return catches.filter(c => normalizePhone(c.userId) === target).slice(0, 6);
  }, [verdictUser, catches]);
  const canSubmit = phone.trim().length >= 4 || name.trim().length >= 2;
  // Auto-verification: structural realness check on the typed number. No SMS is
  // sent (GDPR: the number is only an account identifier — see lib/phoneVerify).
  const phoneCheck = useMemo(() => phone.trim() ? checkPhone(phone) : null, [phone]);
  const fmtDate = (ms: number) => ms ? new Date(ms).toLocaleDateString() : '—';
  return <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800" data-id="element-27" data-mp="7gj66nbk46ov">
      <h2 className="text-lg font-bold mb-1 flex items-center" data-id="element-28" data-mp="2lvmd1ri00gm">
        <ShieldCheck className="w-5 h-5 mr-2 text-brand-yellow" data-id="element-29" data-mp="3tuja1od7nzrc" />
        Verify Returning Member
      </h2>
      <p className="text-gray-400 text-sm mb-4" data-id="element-30" data-mp="v4swa1fszglt">
        Confirm a person who claims to be a returning member. Enter their phone
        (and/or name) to check it against the system.
      </p>

      <form onSubmit={e => {
      e.preventDefault();
      setSubmitted(true);
    }} className="space-y-3" data-id="element-31" data-mp="xoyxi1d18syo">
        <div data-id="element-32" data-mp="12pd0h4zs1cpu">
          <label className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-33" data-mp="17md4lad2g5eb">
            Phone number
          </label>
          <Input placeholder="07X XXX XX XX" type="tel" value={phone} onChange={e => {
          setPhone(e.target.value);
          setSubmitted(false);
        }} className="bg-gray-800 border-gray-700 text-white mt-1" data-id="element-34" data-mp="1clq1knxnisum" />
          {phoneCheck && <div className={`mt-2 flex items-start gap-2 rounded-lg px-3 py-2 text-xs ${phoneCheck.valid ? 'bg-green-500/10 text-green-300' : 'bg-amber-500/10 text-amber-300'}`} data-id="element-35" data-mp="1qdcxgmnolzcv">
              {phoneCheck.valid ? <CheckCircle2 className="w-4 h-4 mt-px shrink-0" data-id="element-36" data-mp="24ccnnyquemb5" /> : <XCircle className="w-4 h-4 mt-px shrink-0" data-id="element-37" data-mp="321inwidrqnq2" />}
              <span data-id="element-38" data-mp="31ze935ec04b0">
                <span className="font-mono" data-id="element-39" data-mp="2x2ffcnykxzdc">{phoneCheck.e164}</span> —{' '}
                {phoneCheck.reason}
              </span>
            </div>}
        </div>
        <div data-id="element-40" data-mp="3at106i7lgjdv">
          <label className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-41" data-mp="3avna2ohyjaz9">
            Name (optional)
          </label>
          <Input placeholder="First & last name" value={name} onChange={e => {
          setName(e.target.value);
          setSubmitted(false);
        }} className="bg-gray-800 border-gray-700 text-white mt-1" data-id="element-42" data-mp="2j24p9n53got8" />
        </div>

        <Button type="submit" disabled={!canSubmit} className="w-full" data-id="element-43" data-mp="2n3q8rrc4w1ce">
          <Search className="w-4 h-4 mr-2" data-id="element-44" data-mp="7d3q099fnbfj" />
          Check member
        </Button>
      </form>

      {/* Name suggestions (when only a name was typed) */}
      {!submitted && nameSuggestions.length > 0 && <div className="mt-4" data-id="element-45" data-mp="av9kpjbmnk4y">
          <p className="text-xs text-gray-500 mb-2" data-id="element-46" data-mp="atyelcupm0ao">
            Matching names — tap to fill the phone:
          </p>
          <div className="space-y-1.5" data-id="element-47" data-mp="ey67lm87a2ep">
            {nameSuggestions.map(u => <button key={u.phone} onClick={() => {
          setPhone(u.phone);
          setName(u.name);
        }} className="w-full flex items-center justify-between bg-gray-800 hover:bg-gray-700 rounded-lg px-3 py-2 text-left transition-colors" data-id="element-48" data-mp="y5747vukwzr">
                <span className="text-sm text-white font-medium" data-id="element-49" data-mp="ymf05xdnoaeq">{u.name}</span>
                <span className="text-xs text-gray-400 font-mono" data-id="element-50" data-mp="jvfuau47fzyu">
                  {u.phone}
                </span>
              </button>)}
          </div>
        </div>}

      {/* Verdict */}
      <AnimatePresence mode="wait" data-id="element-51" data-mp="1ciactlhgop9o">
        {submitted && <motion.div key={verdictUser ? 'found' : 'not-found'} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 8
      }} transition={{
        duration: 0.2
      }} className="mt-5" data-id="element-52" data-mp="1hbvdjlafvxck">
            {verdictUser ? <div className="rounded-2xl border-2 border-green-500/40 bg-green-500/10 p-5" data-id="element-53" data-mp="187y65z6fawaj">
                <div className="flex items-center space-x-2 mb-4" data-id="element-54" data-mp="13hrfltscp1pn">
                  <ShieldCheck className="w-6 h-6 text-green-400" data-id="element-55" data-mp="13fep2s8j1j0z" />
                  <span className="text-green-300 font-bold uppercase tracking-wider text-sm" data-id="element-56" data-mp="yj6ezt96sg0o">
                    Verified member
                  </span>
                </div>

                <div className="space-y-2.5 text-sm" data-id="element-57" data-mp="phzw5dj2et49">
                  <Row icon={UserRound} label="Name" value={verdictUser.name || '—'} data-id="element-58" data-mp="pjt1mphxz7ao" />
                  <Row icon={Phone} label="Phone" value={verdictUser.phone} mono data-id="element-59" data-mp="2w578gjfcxkdc" />
                  <Row icon={Trophy} label="Catches" value={`${verdictUser.catchCount} • ${verdictUser.points} pts (${verdictUser.level})`} data-id="element-60" data-mp="356f8p71fova2" />
                  <Row icon={CalendarClock} label="Last seen" value={fmtDate(verdictUser.lastSeenAt)} data-id="element-61" data-mp="2dgshfw51i6cj" />
                </div>

                {priorCatches.length > 0 && <div className="mt-4 pt-4 border-t border-green-500/20" data-id="element-62" data-mp="2r6w2ijs6nu0e">
                    <p className="text-xs font-bold text-green-300/80 uppercase tracking-wider mb-2" data-id="element-63" data-mp="2r8z0pr3hotg7">
                      Catch history
                    </p>
                    <div className="space-y-1.5" data-id="element-64" data-mp="1ypjphgdo08w8">
                      {priorCatches.map((c, i) => <div key={i} className="flex items-center justify-between text-xs" data-id="element-65" data-mp="s4260qcpsw91" data-mpi={i}>
                          <span className="text-gray-200" data-id="element-66" data-mp="114gtnwd5infp">
                            {c.eventBrand || c.eventTitle || 'Drop'}
                          </span>
                          <span className="text-gray-400 uppercase tracking-wide" data-id="element-67" data-mp="x4gaxzj0rq2u">
                            {c.status}
                          </span>
                        </div>)}
                    </div>
                  </div>}

                <p className="text-green-300/70 text-xs mt-4" data-id="element-68" data-mp="1krersyb1x01a">
                  This number matches a real account. Safe to treat as a
                  returning member.
                </p>
              </div> : <div className="rounded-2xl border-2 border-amber-500/40 bg-amber-500/10 p-5" data-id="element-69" data-mp="1tq9x8869u63j">
                <div className="flex items-center space-x-2 mb-3" data-id="element-70" data-mp="1orglolajop60">
                  <ShieldAlert className="w-6 h-6 text-amber-400" data-id="element-71" data-mp="28gmp35u8ezn2" />
                  <span className="text-amber-300 font-bold uppercase tracking-wider text-sm" data-id="element-72" data-mp="2dexzbhofvc5t">
                    Not found
                  </span>
                </div>
                <p className="text-amber-100/90 text-sm leading-relaxed" data-id="element-73" data-mp="1zgzqersw07xp">
                  No account matches{' '}
                  <span className="font-mono" data-id="element-74" data-mp="1pp3nn3fuvtn5">
                    {phone.trim() || `"${name.trim()}"`}
                  </span>
                  . This person is <strong data-id="element-75" data-mp="1uh4wsghq948y">not</strong> a recognized returning
                  member — they should register as a first catch.
                </p>
              </div>}
          </motion.div>}
      </AnimatePresence>

      {/* GDPR / SMS-verification decision, documented for staff. */}
      <div className="mt-5 rounded-xl border border-gray-800 bg-gray-950 p-4" data-id="element-76" data-mp="16qhm5ngfg0ip">
        <p className="text-xs font-bold text-gray-300 uppercase tracking-wider mb-1.5" data-id="element-77" data-mp="1bo12qu0lkpdt">
          Why there's no SMS verification
        </p>
        <p className="text-xs text-gray-500 leading-relaxed" data-id="element-78" data-mp="ayifapkt3zhg">
          Under GDPR data minimisation, we don't send SMS verification codes:
          the phone number is used only to recognise returning members, and drop
          updates are delivered in-app and via Instagram — never by SMS. This
          panel instead auto-checks that a number is well-formed and plausibly
          real before you treat someone as a verified member.
        </p>
      </div>
    </div>;
}
function Row({
  icon: Icon,
  label,
  value,
  mono = false
}: {
  icon: ComponentType<{
    className?: string;
  }>;
  label: string;
  value: string;
  mono?: boolean;
}) {
  return <div className="flex items-center justify-between" data-id="element-79" data-mp="ps54nkuc3l8h">
      <span className="flex items-center text-gray-400" data-id="element-80" data-mp="1djala283mhju">
        <Icon className="w-4 h-4 mr-2 text-gray-500" data-id="element-81" data-mp="1dhpp6864yqjv" />
        {label}
      </span>
      <span className={`text-white font-medium ${mono ? 'font-mono' : ''}`} data-id="element-82" data-mp="1iaitww1ltyvy">
        {value}
      </span>
    </div>;
}